import { supabase } from "@/lib/supabase/browser-client"
import { Tables } from "@/supabase/types"
import { RealtimePostgresChangesPayload } from "@supabase/supabase-js"
import { Dispatch, SetStateAction } from "react"
import { printObject } from '@/lib/utils'
import { HandlerConfig } from "../base-subscription-manager"
import { v4 as uuidv4 } from "uuid"
import { createClientLogger } from "@/utils/logger/clientLogger"

const logger = createClientLogger('realtime.chat', supabase)

type Row = Tables<'chats'>

const appliesToWorkspace = (workspaceId:string | null, id: string): boolean => {
	if (!workspaceId) return false
	return id === workspaceId
}

/**
 * This method doesn't handle insert since at that time the workspaces are not known.
 * It also doesn't handle delete since workspace relationship join table is better suited since it covers all cases
 *
 * Here we ignore any changes from these table:
 * - assistant_collections
 * - assistant_tools
 * - assistant_files
 * since they don't use state and are manually loaded via fetchDataFunctions
 *
 * @param workspaceId
 * @param setAssistant
 * @returns
 */
export function makeChatHandler(
	workspaceIdRef: React.RefObject<string | null>,
	setChats: Dispatch<SetStateAction<Row[]>>
): HandlerConfig<Row> {
	const uuid = uuidv4()
	return {
		key: `chats-${uuid}`,
		channelName: `chats-${uuid}`,
		events: ['INSERT', 'UPDATE', 'DELETE'],
		schema: 'public',
		table: 'chats',
		handler: async (payload: RealtimePostgresChangesPayload<Row>) => {
			const workspaceId = workspaceIdRef.current
			if (payload.eventType == "INSERT") {
				const chat = payload.new as Row
				if (appliesToWorkspace(workspaceId, chat.workspace_id)) {
					setChats(prev => {
						if (prev.some(p => p.id === chat.id)) return prev
						logger.info(`Handling chat Insert:${printObject(chat)}`)
						return [chat, ...prev]
					})
				}
			}
			else if (payload.eventType === 'UPDATE') {
				const chat = payload.new as Row
				setChats(prev => {
					const exists = prev.some(p => p.id === chat.id)
					if (!exists) return prev // No change, no re-render
					logger.info(`Handling chat update:${printObject(chat)}`)
					return prev.map(p => p.id === chat.id ? chat : p)
				})
			}
			else if (payload.eventType == "DELETE") {
				const provider = payload.old as Row
				setChats(prev => {
					const exists = prev.some(p => p.id === provider.id)
					if (!exists) return prev // No change, no re-render
					logger.info(`Handling chat Delete:${provider.id}`)
					return prev.filter(p => p.id !== provider.id)
				})
			}
		},
		inactivityThreshold: 1_800_000
	}
}